import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { Person } from "@b2bportal/air-booking-api";
import { FlightExchangePolicySelectors } from "@b2bportal/core-flights";
import { ProductType } from "@b2bportal/core-types";
import { useCheckoutSend, useCheckoutStateSelector } from "@checkout/context";
import { ContactSelectors } from "@checkout/states";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CheckoutEvent, type TEvent } from "@hsbc/flights";

export const useFlightExchangeReviewPage = () => {
  const send = useCheckoutSend<TEvent>();
  const contactInfo = useCheckoutStateSelector(
    ContactSelectors.getContactInformation
  );
  const originalBookingPassengers = useSelector(
    FlightExchangePolicySelectors.getPassengers
  );
  const { t } = useI18nContext();
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  const handleContinue = useCallback(() => {
    send({ type: CheckoutEvent.NEXT, productType: ProductType.FLIGHTS });
  }, [send]);

  const allPassengers = useMemo(() => {
    const { alone = [], withLapInfants = [] } = originalBookingPassengers ?? {};
    const persons: Person[] = [];

    if (alone.length) {
      persons.push(...alone.map((p) => p.person));
    }

    if (withLapInfants.length) {
      // lap infants are not common
      persons.push(
        ...withLapInfants.flatMap((p) => [p.adult.person, p.infant.person])
      );
    }

    return persons;
  }, [originalBookingPassengers]);

  return useMemo(() => {
    return {
      context: {
        contactInfo,
        termsAccepted,
        passengers: allPassengers,
      },
      handlers: {
        handleContinue,
        onTermsAndConditionsClick: setTermsAccepted,
      },
      text: {
        checkoutTitle: t("checkoutSection.title"),
        confirmAndBook: t("confirmAndBook"),
        reviewSubtitle: t("core-checkout.review.subtitle"),
        reviewTitle: t("core-checkout.review.title"),
      },
    };
  }, [allPassengers, contactInfo, handleContinue, t, termsAccepted]);
};

export type IFlightExchangeReviewPageData = ReturnType<
  typeof useFlightExchangeReviewPage
>;
